/**
 * @format
 * @flow
 */

import {
  ADD_PRODUCT,
  DELETE_PRODUCT,
  UPDATE_PRODUCT,
  UPDATE_CART_TOTAL,
  UPDATE_COMMENT,
  UPDATE_TYPE,
  UPDATE_CART_PICKUP_DATE,
  UPDATE_RESTO_INFO,
  SET_FLUSH_CART,
  RESET_CART,
  APPLY_DISCOUNT,
  RETURN_CART_FEES,
  SET_CART_TOTAL_CONFIRM,
  APPLY_COUPON,
  PICKED_ORDER_TYPE,
  UPDATE_TABLE,
  UPDATE_QR_ID,
  SET_DISCOUNT_SETTINGS,
  INIT_SHARED_SESSION,
  SYNC_SHARED_CART,
  UPDATE_SESSION_USERS,
  LOCK_SHARED_SESSION,
  LEAVE_SHARED_SESSION,
} from '../actions/cartAction';
import { CartType, ProductType } from '../flowObjectType'

const cartObject = {
  // Firebase-based cart data (synced from Firebase Realtime Database)
  createdAt: null,
  restoId: '',
  restoName: '',
  restoPostal: '',
  restoInformations: {},
  canOrder: false,
  canOrderDelivery: false,
  orderType: 0,
  products: [],
  total: 0,
  totalHT: 0,
  totalVat: 0,
  totalConfirm: 0,
  totalProducts: 0,
  subtotal: 0,
  discount: 0,
  comment: '',
  customerNbr: 0,
  table: '',
  flushCart: false,
  pickupTime: null,
  coupon: null,
  pickedOrderType: false,
  qrId: null,
  settings: { deliveryDiscount: null, takeawayDiscount: null },
  // Shared cart session data
  sessionId: null,
  deviceId: null,
  activeUsers: {},
  sessionState: 'active'
}

export default function foo(state: Object = { ...cartObject }, action: Object) {
  switch (action.type) {
    case UPDATE_COMMENT:
      return { ...state, comment: action.payload.comment }
    case UPDATE_TYPE: {
      const { total, totalHT, totalVat, totalProducts, subtotal, products } = getCartPrice(state.products, action.payload.orderType, state.discount, state.settings);
      return {
        ...state,
        total,
        totalHT,
        totalVat,
        totalProducts,
        products,
        subtotal,
        orderType: action.payload.orderType
      }
    }
    case ADD_PRODUCT:
      return {
        ...state,
        createdAt: state.createdAt === null ? new Date() : state.createdAt,
        products: [...state.products, action.payload.product],
      }
    case UPDATE_PRODUCT:
      state.products[action.payload.idx] = action.payload.product
      return { ...state, products: state.products }
    case DELETE_PRODUCT:
      state.products.splice(action.payload.idx, 1)
      return { ...state, products: state.products }
    case UPDATE_CART_TOTAL:
      return {
        ...state,
        total: action.payload.cartTotal,
        totalHT: action.payload.cartTotalHT,
        totalVat: action.payload.cartTotalVat,
        totalProducts: action.payload.cartTotalProducts,
        subtotal: action.payload.cartSubtotal,
      }
    case UPDATE_RESTO_INFO:
      return {
        ...state,
        restoId: action.payload.restoId,
        restoName: action.payload.restoName,
        restoPostal: action.payload.restoPostal,
        canOrder: action.payload.canOrder,
        canOrderDelivery: action.payload.canOrderDelivery,
        restoInformations: action.payload.restoInformations,
      }
    case SET_FLUSH_CART:
      return {
        ...state,
        flushCart: true,
      }
    case RESET_CART:
      return {
        ...state,
        createdAt: null,
        coupon: null,
        flushCart: false,
        orderType: 0,
        products: [],
        table: '',
        // Conditionally clear qrId based on action payload
        // Non-shared mode: preserve qrId for multiple consecutive orders
        // Shared mode: clear qrId to allow Firebase sync to restore it
        qrId: action.payload?.clearQrId ? null : state.qrId,
        total: 0,
        totalHT: 0,
        totalVat: 0,
        totalProducts: 0,
        totalConfirm: 0,
        pickedOrderType: false
      }
    case UPDATE_CART_PICKUP_DATE:
      return {
        ...state,
        pickupDate: action.payload.pickupDate,
      }
    case RETURN_CART_FEES:
      return {
        ...state,
        discountTotal: action.payload.discountTotal,
        feeTotal: action.payload.feeTotal,
      }
    case APPLY_DISCOUNT: {
      const { total, totalHT, totalVat, totalProducts, products, subtotal } = getCartPrice(state.products, state.orderType, action.payload.discount, state.settings);
      return {
        ...state,
        total,
        totalHT,
        totalVat,
        totalProducts,
        products,
        subtotal,
        discount: action.payload.discount,
      }
    }
    case SET_CART_TOTAL_CONFIRM:
      return {
        ...state,
        totalConfirm: action.payload.totalConfirm,
      }
    case APPLY_COUPON: {
      return {
        ...state,
        coupon: action.payload.coupon
      }
    }
    case PICKED_ORDER_TYPE: {
      return {
        ...state,
        pickedOrderType: true
      }
    }
    case UPDATE_TABLE: {
      return {
        ...state,
        table: action.payload.table
      }
    }
    case UPDATE_QR_ID: {
      return {
        ...state,
        qrId: action.payload.qrId,
      }
    }
    case SET_DISCOUNT_SETTINGS: {
      return {
        ...state,
        settings: action.payload.settings,
      }
    }
    case INIT_SHARED_SESSION: {
      return {
        ...state,
        sessionId: action.payload.sessionId,
        activeUsers: action.payload.sessionData.activeUsers || {},
        sessionState: action.payload.sessionData.sessionState || 'active'
      }
    }
    case SYNC_SHARED_CART: {
      // Sync entire cart data from Firebase
      return {
        ...state,
        ...action.payload
      }
    }
    case UPDATE_SESSION_USERS: {
      return {
        ...state,
        activeUsers: action.payload.activeUsers
      }
    }
    case LOCK_SHARED_SESSION: {
      return {
        ...state,
        sessionState: 'locked'
      }
    }
    case LEAVE_SHARED_SESSION: {
      return {
        ...state,
        sessionId: null,
        deviceId: null,
        activeUsers: {},
        sessionState: 'active'
      }
    }
    default:
      return state;
  }
}

const getProductPrice = (product: ProductType, quantity: number, orderType: number, discount: number, applyDiscount: boolean): { total: number, totalHT: number, totalVat: number, subtotal: number } => {
  if (typeof product === 'undefined' || !product) return { total: 0, totalHT: 0, totalVat: 0, subtotal: 0 }
  
  const rubric = Array.isArray(product.rubric) ? product.rubric : []
  let total = (quantity || 0) * (product.unitPrice || 0)
  let totalHT = (quantity || 0) * (product.unitPriceHT || 0)

  rubric.forEach((section) => {
    if (section && Array.isArray(section.data)) {
      section.data.forEach((rubric) => {
        if (rubric && rubric.checked && (rubric.unitPrice || 0) > 0) {
          if (section.allowSameMultiple) {
            total += (quantity || 0) * (rubric.unitPrice || 0) * (rubric.quantity || 1)
            totalHT += (quantity || 0) * (rubric.unitPriceHT || 0) * (rubric.quantity || 1)
          }
          else {
            total += (quantity || 0) * (rubric.unitPrice || 0)
            totalHT += (quantity || 0) * (rubric.unitPriceHT || 0)
          }
        }
      })
    }
  })

  // apply product discount
  if (orderType === 1 && product.takeawayDiscount && product.takeawayDiscount.discount) {
    total = total * (1 - product.takeawayDiscount.discount / 100)
    totalHT = totalHT * (1 - product.takeawayDiscount.discount / 100)
  }
  else if (orderType === 4 && product.deliveryDiscount && product.deliveryDiscount.discount) {
    total = total * (1 - product.deliveryDiscount.discount / 100)
    totalHT = totalHT * (1 - product.deliveryDiscount.discount / 100)
  }
  else if ((orderType === 2 || orderType === 3) && product.tableDiscount && product.tableDiscount.discount) {
    total = total * (1 - product.tableDiscount.discount / 100)
    totalHT = totalHT * (1 - product.tableDiscount.discount / 100)
  }

  let subtotal = total

  // apply cart discount
  if (discount && applyDiscount) {
    if (
      (orderType === 1 && (!product.takeawayDiscount || (product.takeawayDiscount && product.takeawayDiscount.canDiscount))) ||
      (orderType === 4 && (!product.deliveryDiscount || (product.deliveryDiscount && product.deliveryDiscount.canDiscount))) ||
      ((orderType === 2 || orderType === 3) && (!product.tableDiscount || (product.tableDiscount && product.tableDiscount.canDiscount)))
    ) {
      total = total * (1 - discount / 100)
      totalHT = totalHT * (1 - discount / 100)
    }
  }

  return { total, totalHT, totalVat: total - totalHT, subtotal }
}


const getCartSubtotal = (products, orderType) => {
  if (!Array.isArray(products)) return 0
  
  let cartSubtotalWithoutDiscount = 0
  products.forEach((product) => {
    if (!product || typeof product !== 'object') return
    
    cartSubtotalWithoutDiscount += (product.quantity || 0) * (product.unitPrice || 0)
    
    if (Array.isArray(product.rubric)) {
      product.rubric.forEach((section) => {
        if (section && Array.isArray(section.data)) {
          section.data.forEach((rubric) => {
            if (rubric && rubric.checked && (rubric.unitPrice || 0) > 0) {
              if (section.allowSameMultiple) {
                cartSubtotalWithoutDiscount += (product.quantity || 0) * (rubric.unitPrice || 0) * (rubric.quantity || 1)
              }
              else {
                cartSubtotalWithoutDiscount += (product.quantity || 0) * (rubric.unitPrice || 0)
              }
            }
          })
        }
      })
    }
    
    if (orderType === 1 && product.takeawayDiscount && product.takeawayDiscount.discount) {
      cartSubtotalWithoutDiscount = cartSubtotalWithoutDiscount * (1 - product.takeawayDiscount.discount / 100)
    }
    else if (orderType === 4 && product.deliveryDiscount && product.deliveryDiscount.discount) {
      cartSubtotalWithoutDiscount = cartSubtotalWithoutDiscount * (1 - product.deliveryDiscount.discount / 100)
    }
    else if ((orderType === 2 || orderType === 3) && product.tableDiscount && product.tableDiscount.discount) {
      cartSubtotalWithoutDiscount = cartSubtotalWithoutDiscount * (1 - product.tableDiscount.discount / 100)
    }
  })
  return cartSubtotalWithoutDiscount
}

const getCartPrice = (products, orderType, discount, settings) => {
  if (!Array.isArray(products) || products.length === 0) {
    return { total: 0, totalHT: 0, totalVat: 0, totalProducts: 0, subtotal: 0, products: [] }
  }
  let cartTotal = 0
  let cartTotalHT = 0
  let cartTotalVat = 0
  let cartTotalProducts = 0
  let cartSubtotal = 0
  let cartSubtotalWithoutDiscount = getCartSubtotal(products, orderType)
  let applyDiscount = false
  if (settings && orderType === 1 && settings.takeawayDiscount && cartSubtotalWithoutDiscount >= (settings.takeawayDiscount.discountFrom ?? 0)) {
    applyDiscount = true
  } else if (settings && orderType === 4 && settings.deliveryDiscount && cartSubtotalWithoutDiscount >= (settings.deliveryDiscount.discountFrom ?? 0)) {
    applyDiscount = true
  }

  products.forEach((product) => {
    if (!product || typeof product !== 'object') return
    
    const { total, totalHT, totalVat, subtotal } = getProductPrice(product, product.quantity, orderType, discount, applyDiscount)
    // product.total = total;
    // product.totalHT = totalHT;
    // product.totalVat = totalVat;
    // product.subtotal = subtotal;
    cartTotal += total
    cartTotalHT += totalHT
    cartTotalVat += totalVat
    cartTotalProducts += (product.quantity || 0)
    cartSubtotal += subtotal
  })
  return { total: cartTotal, totalHT: cartTotalHT, totalVat: cartTotalVat, totalProducts: cartTotalProducts, subtotal: cartSubtotal, products }
}

